'use client'

import { useEffect, useState } from 'react'
import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import { usePathname } from 'next/navigation'

export default function ConditionalNavigation({ children }: { children: React.ReactNode }) {
  const [isStoreClosed, setIsStoreClosed] = useState(true) // Default to closed to avoid flashing navigation
  const pathname = usePathname()
  
  useEffect(() => {
    // More comprehensive check for store closed status
    const checkStoreStatus = async () => {
      try {
        // First check API
        const response = await fetch('/api/store-status')
        const data = await response.json()
        
        if (data.isClosed) {
          setIsStoreClosed(true)
          return
        }
        
        // If we're on the homepage (root path), do a content check
        if (pathname === '/') {
          // Look for multiple indicators that this is the closed page
          const fullscreenMode = document.querySelector('.min-h-screen.overflow-hidden.relative.bg-\\[\\#062a52\\]')
          const offlineText = document.getElementById('offline-text')?.textContent?.includes('OFFLINE')
          const fulfillmentText = document.querySelector('.text-red-400.font-mono')?.textContent?.includes('FULFILMENT')
          const closedBanner = document.querySelector('.absolute.-top-2.right-10.bg-gradient-to-b.from-\\[\\#ff2a2a\\].to-\\[\\#cc0000\\]')
          
          // If ANY of these are present, we're on the closed page
          if (fullscreenMode || offlineText || fulfillmentText || closedBanner) {
            setIsStoreClosed(true)
            return
          }
        }
        
        // If we get here, the store is likely open
        setIsStoreClosed(false)
      } catch (error) {
        console.error('Error checking store status:', error)
        // In case of error, keep navigation hidden until we're certain
        setIsStoreClosed(true)
      }
    }
    
    // Immediate check
    checkStoreStatus()
    
    // Re-check after a small delay to ensure content is loaded
    const timer = setTimeout(checkStoreStatus, 300)
    
    return () => clearTimeout(timer)
  }, [pathname])
  
  return (
    <div className="flex flex-col min-h-[100vh]">
      {!isStoreClosed && <Navigation />}
      <main className="flex-1 flex flex-col">{children}</main>
      {!isStoreClosed && <Footer />}
    </div>
  )
} 